/**
 * Анкета-онбординг: варианты ответов, порядок шагов, валидация, черновик,
 * сборка запроса регистрации. Без React — только данные и чистые функции.
 *
 * Значения совпадают с CHOICES_* в main/models.py:
 *   CHOICES_GENDER            0 Мужчина, 1 Женщина, 2 Пара МЖ, 3 Пара ЖЖ, 4 Пара ММ
 *   CHOICES_GENDER_INTEREST   0 Мужчину, 1 Женщину, 2 Пару МЖ, 3 Пару ЖЖ, 4 Пару ММ
 *   CHOICES_PURPOSES_OF_SEARCH 0 Брак, 1 Общение, 2 Весёлое время
 *   CHOICES_INCOME            0..5
 */

// ============================== ВАРИАНТЫ ==============================
// icon — имя из набора в components/Auth/ui.jsx (ChoiceIcon).
// Эмодзи убраны: они рисуются системным шрифтом и на каждом телефоне выглядят
// по-своему, фирменного вида с ними не добиться.
export const GENDERS = [
    { value: 'man', label: 'Мужчина', icon: 'man' },
    { value: 'woman', label: 'Женщина', icon: 'woman' },
    { value: 'couple', label: 'Пара', icon: 'couple', hint: 'Ищем вместе' },
];

export const COUPLE_TYPES = [
    { value: 2, label: 'Мужчина + Женщина', icon: 'couple-mw' },
    { value: 3, label: 'Женщина + Женщина', icon: 'couple-ww' },
    { value: 4, label: 'Мужчина + Мужчина', icon: 'couple-mm' },
];

export const INTERESTS = [
    { value: 0, label: 'Мужчину', icon: 'man' },
    { value: 1, label: 'Женщину', icon: 'woman' },
    { value: 2, label: 'Пару М + Ж', icon: 'couple-mw' },
    { value: 3, label: 'Пару Ж + Ж', icon: 'couple-ww' },
    { value: 4, label: 'Пару М + М', icon: 'couple-mm' },
];

export const PURPOSES = [
    { value: 0, label: 'Брак', icon: 'rings', hint: 'Серьёзные отношения и семья' },
    { value: 1, label: 'Общение', icon: 'chat', hint: 'Дружба и новые знакомства' },
    { value: 2, label: 'Весёлое время', icon: 'fun', hint: 'Лёгкие встречи без обязательств', badge: '18+' },
];

// level — сколько столбиков закрашено в иконке уровня дохода
export const INCOMES = [
    { value: 0, label: 'до 100 000 ₽', level: 0 },
    { value: 1, label: '100 000 – 300 000 ₽', level: 1 },
    { value: 2, label: '300 000 – 500 000 ₽', level: 2 },
    { value: 3, label: '500 000 – 1 млн ₽', level: 3 },
    { value: 4, label: '1 – 5 млн ₽', level: 4 },
    { value: 5, label: 'более 5 млн ₽', level: 5 },
];

export const POPULAR_CITIES = [
    'Москва', 'Санкт-Петербург', 'Екатеринбург', 'Новосибирск',
    'Казань', 'Пермь', 'Краснодар', 'Сочи',
];

export const ABOUT_PROMPTS = [
    'Люблю путешествовать', 'Вечер с хорошим фильмом', 'Обожаю животных',
    'Спорт — часть жизни', 'Много читаю', 'Отлично готовлю',
];

export const AGE_MIN = 18;
export const AGE_MAX = 99;
export const ABOUT_MAX = 500;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;   // после сжатия
const MAX_PICK_BYTES = 25 * 1024 * 1024;     // исходник с телефона


// ============================== ОТВЕТЫ ==============================
export const INITIAL_ANSWERS = {
    genderChoice: null,     // 'man' | 'woman' | 'couple' — выбор на первом экране
    gender: null,           // 0..4 — уходит на бэк
    interest: null,         // gender_interest
    purpose: null,          // purpose_of_search
    name: '',
    age: 25,
    city: '',
    height: 170, heightTouched: false,
    weight: 65, weightTouched: false,
    income: null,
    field: null,            // { id: number|null, name: string }
    hobbies: [], customHobbies: [],
    partnerHobbies: [], partnerCustomHobbies: [],
    about: '',
    email: '', phone: '', password: '', consent: false,
};

/** Выбор на первом экране. Мужчина ищет женщину, женщина — мужчину (как в старой анкете). */
export function genderPatch(choice) {
    if (choice === 'man') return { genderChoice: 'man', gender: 0, interest: 1, purpose: null };
    if (choice === 'woman') return { genderChoice: 'woman', gender: 1, interest: 0, purpose: null };
    return { genderChoice: 'couple', gender: null, interest: null, purpose: null };
}

/** Выбор «кого ищу» (только для пар). Для поиска пары цель одна — «Весёлое время». */
export function interestPatch(value, a) {
    if (value >= 2) return { interest: value, purpose: 2 };
    return { interest: value, purpose: a.interest === value ? a.purpose : null };
}


// ============================== ШАГИ ==============================
/**
 * stage:
 *   mini    — короткая анкета, её проходит гость ДО регистрации;
 *   account — сам шаг регистрации;
 *   full    — остальное, заполняется уже после входа, до ленты.
 * Порядок в массиве = порядок показа.
 */
export const STEPS = [
    { id: 'gender', stage: 'mini' },
    { id: 'coupleType', stage: 'mini', when: (a) => a.genderChoice === 'couple' },
    { id: 'interest', stage: 'mini', when: (a) => a.genderChoice === 'couple' },
    { id: 'purpose', stage: 'mini', when: (a) => a.interest === 0 || a.interest === 1 || a.interest === null },
    { id: 'name', stage: 'mini' },
    { id: 'age', stage: 'mini' },
    { id: 'city', stage: 'mini' },
    { id: 'account', stage: 'account' },
    { id: 'body', stage: 'full' },
    { id: 'income', stage: 'full' },
    { id: 'field', stage: 'full' },
    { id: 'hobbies', stage: 'full' },
    { id: 'partnerHobbies', stage: 'full', when: (a) => a.interest !== null && a.interest >= 2 },
    { id: 'about', stage: 'full' },
    { id: 'photo', stage: 'full' },
];

export const activeSteps = (a) => STEPS.filter((s) => !s.when || s.when(a));

/**
 * Шаги для режима визарда:
 *   register — мини-анкета + регистрация (гость);
 *   profile  — всё, кроме регистрации (вошёл, анкета не дозаполнена).
 */
export function stepsForMode(mode, a) {
    const list = activeSteps(a);
    return mode === 'register'
        ? list.filter((s) => s.stage !== 'full')
        : list.filter((s) => s.stage !== 'account');
}

/**
 * На каком шаге ответа ещё нет. Нужно, чтобы вошедший пользователь продолжал
 * анкету с того места, где остановился, а не проходил заново уже заполненное.
 * Рост/вес и «о себе» формально необязательны, но без ответа считаем их незаполненными —
 * иначе после мини-анкеты человек сразу прыгнет на доход.
 */
const ANSWERED = {
    gender: (a) => a.gender !== null,
    coupleType: (a) => a.gender !== null && a.gender >= 2,
    interest: (a) => a.interest !== null,
    purpose: (a) => a.purpose !== null,
    name: (a) => a.name.trim().length >= 2,
    age: (a) => Boolean(a.age),
    city: (a) => a.city.trim().length >= 2,
    body: (a) => a.heightTouched || a.weightTouched,
    income: (a) => a.income !== null,
    field: (a) => Boolean((a.field?.name || '').trim()),
    hobbies: (a) => a.hobbies.length > 0 || a.customHobbies.length > 0,
    partnerHobbies: (a) => a.partnerHobbies.length > 0 || a.partnerCustomHobbies.length > 0,
    about: (a) => a.about.trim().length > 0,
    photo: (_a, ctx) => Boolean(ctx.photo),
};

export function firstUnansweredStep(a, steps, ctx = {}) {
    const step = steps.find((s) => !(ANSWERED[s.id] || (() => true))(a, ctx));
    return (step || steps[steps.length - 1] || { id: 'gender' }).id;
}

/** Анкета готова: и мини-часть, и полная (или полную сервер не требует). */
export const isProfileDone = (status) => Boolean(
    status?.has_profile && status?.basic_complete
    && (status?.full_complete || status?.require_full === false),
);


// ============================== ВАЛИДАЦИЯ ==============================
export const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());

/** Только цифры номера РФ/КЗ: всегда начинается с 7, максимум 11 цифр. */
export function phoneDigits(v) {
    let d = String(v || '').replace(/\D/g, '');
    if (!d) return '';
    if (d[0] === '8') d = `7${d.slice(1)}`;
    if (d[0] !== '7') d = `7${d}`;
    return d.slice(0, 11);
}

/** +7 (912) 345-67-89 — скобка появляется только после 4-й цифры, чтобы стирание работало. */
export function formatPhone(v) {
    const d = phoneDigits(v);
    if (!d) return '';
    const p = d.slice(1);
    let out = '+7';
    if (p.length > 0) out += ` (${p.slice(0, 3)}`;
    if (p.length > 3) out += `) ${p.slice(3, 6)}`;
    if (p.length > 6) out += `-${p.slice(6, 8)}`;
    if (p.length > 8) out += `-${p.slice(8, 10)}`;
    return out;
}

export function passwordError(pw) {
    if (!pw) return 'Придумайте пароль';
    if (pw.length < 8) return 'Минимум 8 символов';
    if (!/[A-Za-zА-Яа-яЁё]/.test(pw) || !/\d/.test(pw)) return 'Нужны и буквы, и цифры';
    return null;
}

/** Ошибка шага или null. Шаги с выбором карточкой не валидируются — там нельзя «не выбрать». */
export function validateStep(id, a, ctx = {}) {
    switch (id) {
        case 'name': {
            const n = a.name.trim();
            if (n.length < 2) return 'Введите имя — минимум 2 буквы';
            if (!/^[\p{L}\s'’-]+$/u.test(n)) return 'Имя может содержать только буквы';
            return null;
        }
        case 'age':
            return a.age >= AGE_MIN && a.age <= AGE_MAX ? null : 'Возраст — от 18 до 99 лет';
        case 'city':
            return a.city.trim().length >= 2 ? null : 'Укажите город';
        case 'field':
            return (a.field?.name || '').trim().length >= 2 ? null : 'Выберите сферу или впишите свою';
        case 'photo':
            return ctx.photo ? null : 'Добавьте фото, чтобы продолжить';
        default:
            return null;
    }
}

export function validateAccount(a) {
    const e = {};
    if (!isValidEmail(a.email)) e.email = 'Проверьте email';
    if (phoneDigits(a.phone).length !== 11) e.phone = 'Введите номер полностью';
    const pe = passwordError(a.password);
    if (pe) e.password = pe;
    if (!a.consent) e.form = 'Отметьте согласие, чтобы создать анкету';
    return e;
}


// ============================== ЧЕРНОВИК ==============================
// Ответы переживают перезагрузку страницы. Пароль и фото не сохраняем.
// register — гость заполняет анкету до регистрации, profile — вошедший без анкеты
export const DRAFT_KEYS = { register: 'lovirds_onboarding_v1', profile: 'lovirds_profile_v1' };
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export function loadDraft(key = DRAFT_KEYS.register) {
    try {
        const d = JSON.parse(localStorage.getItem(key) || 'null');
        if (!d || Date.now() - (d.savedAt || 0) > DRAFT_TTL_MS) return null;
        return d;
    } catch (_e) { return null; }
}

export function saveDraft(answers, stepId, key = DRAFT_KEYS.register) {
    try {
        const { password, ...safe } = answers;
        localStorage.setItem(key, JSON.stringify({ answers: safe, stepId, savedAt: Date.now() }));
    } catch (_e) { /* приватный режим / переполнение — не критично */ }
}

export function clearDraft(key = DRAFT_KEYS.register) {
    try { localStorage.removeItem(key); } catch (_e) { /* ignore */ }
}

/** С какого шага продолжить. Фото в черновике нет — всё, что после него, начинаем с фото. */
export function restoreStep(draft, mode = 'register') {
    const steps = stepsForMode(mode, { ...INITIAL_ANSWERS, ...(draft?.answers || {}) });
    const ids = steps.map((s) => s.id);
    const first = ids[0] || 'gender';
    if (!draft?.stepId || !ids.includes(draft.stepId)) return first;
    const photoAt = ids.indexOf('photo');
    return photoAt >= 0 && ids.indexOf(draft.stepId) > photoAt ? 'photo' : draft.stepId;
}


// ============================== ФОТО ==============================
/**
 * Уменьшает фото до 1600px по длинной стороне (JPEG 86%). Фото с телефона весят
 * 3–8 МБ — после сжатия обычно 300–600 КБ: быстрее загрузка на мобильном интернете.
 * Если браузер не смог прочитать файл (например, HEIC в Chrome) — вернёт исходник.
 */
export function compressImage(file, maxSide = 1600, quality = 0.86) {
    return new Promise((resolve) => {
        if (!file?.type?.startsWith('image/') || file.type === 'image/gif') { resolve(file); return; }
        const url = URL.createObjectURL(file);
        const img = new Image();
        const done = (result) => { URL.revokeObjectURL(url); resolve(result); };
        img.onload = () => {
            try {
                const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
                const w = Math.round(img.naturalWidth * scale);
                const h = Math.round(img.naturalHeight * scale);
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#fff';               // прозрачный PNG -> белый фон, а не чёрный
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                canvas.toBlob((blob) => {
                    if (!blob || blob.size >= file.size) { done(file); return; }
                    const name = `${(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;
                    done(new File([blob], name, { type: 'image/jpeg' }));
                }, 'image/jpeg', quality);
            } catch (_e) { done(file); }
        };
        img.onerror = () => done(file);
        img.src = url;
    });
}

/** Проверка выбранного файла. Возвращает текст ошибки или null. */
export function photoPickError(file) {
    if (!file) return 'Файл не выбран';
    if (file.type && !file.type.startsWith('image/')) return 'Выберите изображение — JPG или PNG';
    if (file.size > MAX_PICK_BYTES) return 'Файл слишком большой — до 25 МБ';
    return null;
}

export const photoTooBig = (file) => file.size > MAX_UPLOAD_BYTES;


// ============================== ЗАПРОС ==============================
/**
 * Поля анкеты (ProfileCreateSerializer) — общие для создания и редактирования.
 * photoFile необязателен: при редактировании фото отправляем только если меняли.
 */
export function buildProfileFormData(a, photoFile) {
    const fd = new FormData();
    fd.append('name', a.name.trim());
    fd.append('gender', String(a.gender));
    fd.append('age', String(a.age));
    fd.append('city', a.city.trim());
    // Незаполненные поля не отправляем: после мини-анкеты их ещё нет,
    // а строка "null" сервер не примет
    if (a.interest !== null) fd.append('gender_interest', String(a.interest));
    if (a.purpose !== null) fd.append('purpose_of_search', String(a.purpose));
    if (a.heightTouched) fd.append('height', String(a.height));
    if (a.weightTouched) fd.append('weight', String(a.weight));
    if (a.income !== null) fd.append('income', String(a.income));
    const fieldName = (a.field?.name || '').trim();
    if (fieldName) {
        fd.append('field_of_activity', fieldName);
        if (a.field?.id) fd.append('field_ids', String(a.field.id));
        else fd.append('custom_fields', fieldName);
    }
    a.hobbies.forEach((id) => fd.append('hobby_ids', String(id)));
    a.customHobbies.forEach((n) => fd.append('custom_hobbies', n));
    if (a.interest >= 2) {
        a.partnerHobbies.forEach((id) => fd.append('partner_hobby_ids', String(id)));
        a.partnerCustomHobbies.forEach((n) => fd.append('partner_custom_hobbies', n));
    }
    fd.append('message_to_partner', a.about.trim());
    if (photoFile) fd.append('photo_file_path', photoFile, photoFile.name || 'photo.jpg');
    return fd;
}

/** multipart/form-data для POST /api/auth/register-with-profile/ (анкета + аккаунт) */
export function buildRegistrationFormData(a, photoFile, ref) {
    const fd = buildProfileFormData(a, photoFile);
    fd.append('email', a.email.trim().toLowerCase());
    fd.append('phone', `+${phoneDigits(a.phone)}`);
    fd.append('password', a.password);
    if (ref) fd.append('ref', ref);
    return fd;
}

/**
 * Анкета с сервера -> ответы визарда (для редактирования).
 * Пол 2/3/4 — это пара, поэтому genderChoice = 'couple'.
 */
export function answersFromProfile(profile) {
    const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
    const gender = num(profile?.gender) ?? 0;
    const hobbies = profile?.hobbies || [];
    const partnerHobbies = profile?.partner_hobbies || [];
    const fields = profile?.fields_of_activity || [];
    const standardField = fields.find((f) => !f.is_custom);
    return {
        ...INITIAL_ANSWERS,
        genderChoice: gender >= 2 ? 'couple' : (gender === 1 ? 'woman' : 'man'),
        gender,
        interest: num(profile?.gender_interest),
        purpose: num(profile?.purpose_of_search),
        name: profile?.name || '',
        age: num(profile?.age) || 25,
        city: profile?.city || '',
        height: num(profile?.height) || 170,
        heightTouched: Boolean(profile?.height),
        weight: num(profile?.weight) || 65,
        weightTouched: Boolean(profile?.weight),
        income: num(profile?.income),
        field: profile?.field_of_activity
            ? { id: standardField?.id || null, name: profile.field_of_activity }
            : null,
        hobbies: hobbies.filter((h) => !h.is_custom).map((h) => h.id),
        customHobbies: hobbies.filter((h) => h.is_custom).map((h) => h.name),
        partnerHobbies: partnerHobbies.filter((h) => !h.is_custom).map((h) => h.id),
        partnerCustomHobbies: partnerHobbies.filter((h) => h.is_custom).map((h) => h.name),
        about: profile?.message_to_partner || '',
    };
}

// Поле ошибки сервера -> шаг анкеты, куда вернуть пользователя
const DETAIL_TO_STEP = {
    gender: 'gender', gender_interest: 'interest', purpose_of_search: 'purpose',
    name: 'name', age: 'age', city: 'city', height: 'body', weight: 'body',
    income: 'income', field_of_activity: 'field', message_to_partner: 'about',
    photo_file_path: 'photo',
};
const FRIENDLY = {
    photo_file_path: 'Не удалось прочитать фото — выберите JPG или PNG',
    age: 'Проверьте возраст: от 18 до 99 лет',
    height: 'Проверьте рост', weight: 'Проверьте вес',
};

/** Ответ сервера -> { stepId?, fieldErrors?, formError? } */
export function mapRegisterError(error, steps) {
    if (!error?.response) return { formError: 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз' };
    const { status, data = {} } = error.response;
    if (status === 429) return { formError: data.error || 'Слишком много попыток. Попробуйте чуть позже' };
    if (['email', 'phone', 'password'].includes(data.field)) return { fieldErrors: { [data.field]: data.error } };
    if (data.field === 'photo') return { stepId: 'photo', formError: data.error };
    if (data.details && typeof data.details === 'object') {
        const key = Object.keys(data.details).find((k) => DETAIL_TO_STEP[k]);
        const target = key && DETAIL_TO_STEP[key];
        if (target && steps.some((s) => s.id === target)) {
            const raw = [].concat(data.details[key])[0];
            return { stepId: target, formError: FRIENDLY[key] || raw || 'Проверьте это поле' };
        }
    }
    return { formError: data.error || 'Не удалось создать анкету. Попробуйте ещё раз' };
}
